// client/src/components/pages/AmendmentsAllocateTransactions/index.tsx (converted from .js, ST-00216)
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ComponentType,
  type ForwardRefExoticComponent,
  type ReactNode,
  type RefAttributes,
} from 'react';
import { FaEdit, FaFilter } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import type { AxiosResponse } from 'axios';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import FilterControls from './FilterControls';
import DataTableJs from './DataTable';
import ColumnSelectorJs from './ColumnSelector';
import VersionHistoryModalAllocateTransactionsJs from './VersionHistoryModalAllocateTransactions';
import BulkAmendmentModalAllocateTransactions from './BulkAmendmentModalAllocateTransactions';
import TransactionDetailsModal from './TransactionDetailsModal';
import ShareTransactionModalJs from './ShareTransactionModal';
import RegNoTransactionsModalAmendmentsJs from './RegNoTransactionsModalAmendments';
import FindBarJs from '../../common/FindBar';
import FindReplaceBar, { type FindReplacement } from './FindReplaceBar';
import EditModeDiffPanelJs from './EditModeDiffPanel';
import { isValidWeight, isWeightField, rejectionMessage } from './amendmentFields';
import CopyableCellJs from './CopyableCell';
import {
  PageHeader,
  SegmentedControl,
  ThemedSelect as ThemedSelectBase,
  type ThemedSelectProps,
} from '../../../types/commonComponents';
import { toApiDateString, formatAppDateTime } from '../../../utils/dateUtils';
import type {
  AmendmentFilters,
  AmendmentRejectionBody,
  AmendmentTicketRow,
  ApiErrorBody,
  BulkAmendIndividualPayload,
  BulkAmendIndividualResponse,
  ExcludeTicketResponse,
  FindMatch,
  FindResponse,
  PendingBatchConflictBody,
  TicketPage,
} from '../../../types/clientPages/amendments';
// ST-00218 lm-status: the "Load status" column + export column, and the load-matching hand-off.
import type { AmendLink } from '../../../types/loadMatching';
import { addLoadStatusToExport, useLoadStatusColumn } from './loadStatusColumn';

type Tab = 'current' | 'history' | 'excluded';

interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
}

interface SortConfig {
  /** Bracketed view column ('[Ticket Timestamp]'); null = the server's default order. */
  sortBy: string | null;
  sortDir: 'ASC' | 'DESC';
  thenBy: string | null;
  thenDir: 'ASC' | 'DESC';
}

/** What the page reads from a rejected request (axios error, or an abort). */
type RequestError = Error & {
  code?: string;
  __CANCEL__?: boolean;
  response?: {
    status?: number;
    data: ApiErrorBody & PendingBatchConflictBody & AmendmentRejectionBody;
  };
};

/** A table cell value (react-table `value`). */
type CellValue = string | number | boolean | null | undefined;

/** The react-table cell props the column renderers read. */
interface TicketCellProps<V = CellValue> {
  value: V;
  row?: { original?: AmendmentTicketRow };
}

/** A react-table v7 column of the amendments tables (DataTable, ColumnSelector, FindReplaceBar). */
interface TicketColumn {
  Header: string;
  accessor: string;
  // Method syntax on purpose: each column's renderer reads its own value type.
  Cell?(props: TicketCellProps): ReactNode;
}

// Typed views of the (still JavaScript) components this page renders.
const DataTable = DataTableJs as unknown as ComponentType<{
  columns: TicketColumn[];
  data: AmendmentTicketRow[];
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onViewHistory?: (ticketUID: string) => void;
  onViewTransactionDetails?: (row: AmendmentTicketRow) => void;
  onShareTransaction?: (row: AmendmentTicketRow) => void;
  onExcludeTicket?: (row: AmendmentTicketRow) => void;
  loading: boolean;
  onSelectedRowsChange?: (rows: AmendmentTicketRow[]) => void;
  selectedRows?: AmendmentTicketRow[];
  onBulkAmend?: () => void;
  onBulkExclude?: () => void;
  highlightTicketUID?: string | null;
  editMode?: boolean;
  pendingEdits?: Map<string, string>;
  onCellEdit?: (ticketUID: string, field: string, newValue: string | null) => void;
  readOnly?: boolean;
}>;
const ColumnSelector = ColumnSelectorJs as unknown as ComponentType<{
  allColumns: TicketColumn[];
  visibleColumns: TicketColumn[];
  onVisibleColumnsChange: (columns: TicketColumn[]) => void;
}>;
const VersionHistoryModalAllocateTransactions =
  VersionHistoryModalAllocateTransactionsJs as unknown as ComponentType<{
    isOpen: boolean;
    onClose: () => void;
    ticketUID: string | null;
  }>;
interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionData: AmendmentTicketRow | null;
}
const ShareTransactionModal =
  ShareTransactionModalJs as unknown as ComponentType<TransactionModalProps>;
const RegNoTransactionsModalAmendments =
  RegNoTransactionsModalAmendmentsJs as unknown as ComponentType<{
    isOpen: boolean;
    onClose: () => void;
    regNo: string | null;
    clickedTicketNo: string | null;
    inheritedFilters: AmendmentFilters;
  }>;
const FindBar = FindBarJs as unknown as ForwardRefExoticComponent<
  {
    value: string;
    onChange: (value: string) => void;
    onSubmit: (term: string) => void;
    onNext: () => void;
    onPrev: () => void;
    onClear: () => void;
    totalMatches: number | null;
    currentIndex: number;
    loading: boolean;
    capped: boolean;
    lastSearchedTerm: string | null;
    errorMessage: string | null;
    placeholder?: string;
  } & RefAttributes<HTMLInputElement>
>;
const EditModeDiffPanel = EditModeDiffPanelJs as unknown as ComponentType<{
  pendingEdits: Map<string, string>;
  originalData: AmendmentTicketRow[];
  amendmentNotes: string;
  onNotesChange: (notes: string) => void;
  onSave: () => void;
  onDiscard: () => void;
  onUndoEdit: (ticketUID: string, field: string) => void;
  isSaving: boolean;
}>;
const CopyableCell = CopyableCellJs as unknown as ComponentType<{
  value: CellValue;
  children?: ReactNode;
  className?: string;
}>;
/** The page-size select takes numeric option values (ThemedSelect matches them by String()). */
const ThemedSelect = ThemedSelectBase as unknown as ComponentType<
  Omit<ThemedSelectProps, 'value'> & { value: string | number }
>;

const AmendmentsAllocateTransactions = () => {
  // Amending (Table Edit Mode, Bulk Amend, Find & Replace, exclude / restore) needs
  // amendments.edit; the server refuses the writes without it (AMD-22). Viewers read only.
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('amendments.edit');
  const [activeTab, setActiveTab] = useState<Tab>('current'); // 'current', 'history' or 'excluded'
  // Removed sortByRegTimestamp state
  const [currentTickets, setCurrentTickets] = useState<AmendmentTicketRow[]>([]); // Paginated data for display
  // Removed fullCurrentTickets state
  const [historyTickets, setHistoryTickets] = useState<AmendmentTicketRow[]>([]);
  const [resultsPerPage, setResultsPerPage] = useState(25); // State for results per page, default 25
  const [currentPagination, setCurrentPagination] = useState<PaginationState>({
    page: 1,
    limit: 25,
    totalPages: 1,
    totalCount: 0,
  }); // Default limit 25
  const [historyPagination, setHistoryPagination] = useState<PaginationState>({
    page: 1,
    limit: 25,
    totalPages: 1,
    totalCount: 0,
  }); // Default limit 25
  // Excluded tickets leave the data view, so they have their own list (AMD-4)
  const [excludedTickets, setExcludedTickets] = useState<AmendmentTicketRow[]>([]);
  const [excludedPagination, setExcludedPagination] = useState<PaginationState>({
    page: 1,
    limit: 25,
    totalPages: 1,
    totalCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Initial filters — consume ?ticketUIDs=... handoff from Recon View (bulk-amend flow)
  const [filters, setFilters] = useState<AmendmentFilters>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const uids = params.get('ticketUIDs');
      if (uids && uids.trim()) {
        const cleaned = uids
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 100)
          .join(',');
        if (cleaned) return { ticketUIDs: cleaned };
      }
    } catch (_) {}
    return {};
  });
  const [handoffTicketUIDs, setHandoffTicketUIDs] = useState<string[] | null>(() => {
    try {
      const uids = new URLSearchParams(window.location.search).get('ticketUIDs');
      if (!uids) return null;
      const list = uids
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 100);
      return list.length > 0 ? list : null;
    } catch (_) {
      return null;
    }
  });
  // ST-00218: the load-matching data-quality panel hands over the correction it suggests
  // (`&field=reg_no|delivery_no&value=…`), a probable duplicate weighing to exclude
  // (`&field=exclude&value=<ticket no of the dispatch it duplicates>`) or the dispatches to check
  // together (`&field=review`), or a ticket whose date is implausible (`&field=ticket_timestamp&value=<date>`);
  // ST-00218 P3: "Location probably wrong" hands over the location to set
  // (`&field=dispatch_location|receipt_location&value=…&why=<the evidence>`), for one ticket or several;
  // shown in the hand-off banner, never applied by itself.
  const [handoffSuggestion] = useState<{
    field: AmendLink['field'];
    value: string | null;
    why: string | null;
  } | null>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const field = params.get('field');
      if (
        !params.get('ticketUIDs') ||
        (field !== 'reg_no' &&
          field !== 'delivery_no' &&
          field !== 'exclude' &&
          field !== 'review' &&
          field !== 'ticket_timestamp' &&
          field !== 'dispatch_location' &&
          field !== 'receipt_location')
      )
        return null;
      const value = (params.get('value') || '').trim().slice(0, 100);
      // the sentence is joined into the banner: no trailing full stop of its own
      const why = (params.get('why') || '')
        .trim()
        .slice(0, 300)
        .replace(/[.\s]+$/, '');
      return { field, value: value || null, why: why || null };
    } catch (_) {
      return null;
    }
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '[Ticket Timestamp]',
    sortDir: 'ASC',
    thenBy: null,
    thenDir: 'ASC',
  });
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false); // State for bulk modal
  const [selectedTicketUID, setSelectedTicketUID] = useState<string | null>(null); // For history modal
  const [selectedRows, setSelectedRows] = useState<AmendmentTicketRow[]>([]); // State for selected rows
  const [isExporting, setIsExporting] = useState(false); // State for export loading
  const [exportError, setExportError] = useState<string | null>(null); // State for export error
  const [isTransactionDetailsModalOpen, setIsTransactionDetailsModalOpen] = useState(false); // For transaction details modal
  const [selectedTransactionData, setSelectedTransactionData] = useState<AmendmentTicketRow | null>(
    null
  ); // For transaction details modal
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareTransactionData, setShareTransactionData] = useState<AmendmentTicketRow | null>(null);
  const [isRegNoModalOpen, setIsRegNoModalOpen] = useState(false);
  const [selectedRegNo, setSelectedRegNo] = useState<string | null>(null);
  const [clickedTicketNoForRegNo, setClickedTicketNoForRegNo] = useState<string | null>(null);
  // If the page loaded with a ticketUIDs handoff, mark filters as already applied so the initial fetch fires
  const [hasAppliedFilters, setHasAppliedFilters] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).has('ticketUIDs');
    } catch (_) {
      return false;
    }
  });
  const abortControllerRef = useRef<AbortController | null>(null); // To store the AbortController

  // --- Find-in-results state (Current tab only) ---
  const [findTerm, setFindTerm] = useState('');
  const [findMatches, setFindMatches] = useState<FindMatch[] | null>(null); // null = not yet searched
  const [findIndex, setFindIndex] = useState(0);
  const [findLoading, setFindLoading] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [findCapped, setFindCapped] = useState(false);
  const [lastSearchedTerm, setLastSearchedTerm] = useState<string | null>(null);
  const [highlightTicketUID, setHighlightTicketUID] = useState<string | null>(null);
  const findInputRef = useRef<HTMLInputElement>(null);
  const findAbortRef = useRef<AbortController | null>(null);
  const handlePageChangeRef = useRef<((tab: Tab, newPage: number) => void) | null>(null);

  // --- Inline Edit Mode state ---
  const [editMode, setEditMode] = useState(false);
  const [pendingEdits, setPendingEdits] = useState<Map<string, string>>(new Map());
  const [editAmendmentNotes, setEditAmendmentNotes] = useState('');
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [editSaveError, setEditSaveError] = useState<string | null>(null);

  // --- Find-in-results handlers ---
  const clearFind = useCallback(() => {
    if (findAbortRef.current) {
      try {
        findAbortRef.current.abort();
      } catch (_) {}
      findAbortRef.current = null;
    }
    setFindTerm('');
    setFindMatches(null);
    setFindIndex(0);
    setFindError(null);
    setFindCapped(false);
    setLastSearchedTerm(null);
    setHighlightTicketUID(null);
  }, []);

  const gotoMatch = useCallback(
    (index: number, matches: FindMatch[] | null) => {
      if (!matches || matches.length === 0) return;
      const count = matches.length;
      const wrapped = ((index % count) + count) % count;
      setFindIndex(wrapped);
      const match = matches[wrapped]!;
      setHighlightTicketUID(match.ticketUID);
      if (match.page && match.page !== currentPagination.page) {
        handlePageChangeRef.current?.('current', match.page);
      }
    },
    [currentPagination.page]
  );

  const runFind = useCallback(
    async (rawTerm: string | null | undefined) => {
      const term = (rawTerm || '').trim();
      if (!term) {
        clearFind();
        return;
      }

      if (findAbortRef.current) {
        try {
          findAbortRef.current.abort();
        } catch (_) {}
      }
      findAbortRef.current = new AbortController();

      setFindLoading(true);
      setFindError(null);
      try {
        const params: Record<string, unknown> = {
          searchTerm: term,
          ...filters,
          limit: resultsPerPage,
        };
        if (sortConfig.sortBy) {
          params.sortBy = sortConfig.sortBy;
          params.sortDir = sortConfig.sortDir;
          if (sortConfig.thenBy) {
            params.thenBy = sortConfig.thenBy;
            params.thenDir = sortConfig.thenDir;
          }
        }
        const res: AxiosResponse<FindResponse | undefined> = await api.amendmentsV3.find(params, {
          signal: findAbortRef.current.signal,
        });
        const data: FindResponse = res.data || {};
        const matches = Array.isArray(data.matches) ? data.matches : [];
        setFindMatches(matches);
        setFindCapped(!!data.capped);
        setLastSearchedTerm(term);
        if (matches.length > 0) {
          gotoMatch(0, matches);
        } else {
          setHighlightTicketUID(null);
          setFindIndex(0);
        }
      } catch (caught) {
        const err = caught as RequestError;
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        console.error('Find error:', err);
        setFindError(err.response?.data?.message || err.message || 'Find failed');
        setFindMatches([]);
        setLastSearchedTerm(term);
      } finally {
        setFindLoading(false);
      }
    },
    [filters, sortConfig, resultsPerPage, gotoMatch, clearFind]
  );

  const findNext = useCallback(() => {
    if (findMatches && findMatches.length > 0) gotoMatch(findIndex + 1, findMatches);
  }, [findMatches, findIndex, gotoMatch]);

  const findPrev = useCallback(() => {
    if (findMatches && findMatches.length > 0) gotoMatch(findIndex - 1, findMatches);
  }, [findMatches, findIndex, gotoMatch]);

  // Reg No click → transactions modal
  const handleRegNoClick = useCallback((regNo: string, ticketNo?: string | null) => {
    if (!regNo) return;
    setSelectedRegNo(regNo);
    setClickedTicketNoForRegNo(ticketNo || null);
    setIsRegNoModalOpen(true);
  }, []);

  const handleCloseRegNoModal = useCallback(() => {
    setIsRegNoModalOpen(false);
    setSelectedRegNo(null);
    setClickedTicketNoForRegNo(null);
  }, []);

  const regNoCell = useCallback(
    ({ value, row }: TicketCellProps<string | null | undefined>) => {
      if (!value) return <span className="text-slate-400">—</span>;
      const ticketNo = row?.original?.['Ticket No'];
      return (
        <CopyableCell value={value}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRegNoClick(value, ticketNo);
            }}
            title={`View all transactions for ${value}`}
            className="font-mono text-primary-700 hover:text-primary-900 hover:underline underline-offset-2 decoration-primary-400/60 transition-colors"
          >
            {value}
          </button>
        </CopyableCell>
      );
    },
    [handleRegNoClick]
  );

  // --- Inline Edit Mode handlers ---
  const handleCellEdit = useCallback(
    (ticketUID: string, field: string, newValue: string | null) => {
      setPendingEdits((prev) => {
        const next = new Map(prev);
        if (newValue === null) {
          next.delete(`${ticketUID}::${field}`);
        } else {
          next.set(`${ticketUID}::${field}`, newValue);
        }
        return next;
      });
    },
    []
  );

  const handleToggleEditMode = useCallback(() => {
    if (editMode && pendingEdits.size > 0) {
      if (!window.confirm(`You have ${pendingEdits.size} unsaved edit(s). Discard them?`)) return;
    }
    setEditMode((prev) => !prev);
    setPendingEdits(new Map());
    setEditAmendmentNotes('');
    setShowFindReplace(false);
    setEditSaveError(null);
  }, [editMode, pendingEdits.size]);

  const handleDiscardEdits = useCallback(() => {
    if (
      pendingEdits.size > 0 &&
      !window.confirm(`Discard all ${pendingEdits.size} pending edit(s)?`)
    )
      return;
    setPendingEdits(new Map());
    setEditAmendmentNotes('');
    setEditSaveError(null);
  }, [pendingEdits.size]);

  const handleUndoEdit = useCallback((ticketUID: string, field: string) => {
    setPendingEdits((prev) => {
      const next = new Map(prev);
      next.delete(`${ticketUID}::${field}`);
      return next;
    });
  }, []);

  // Generic copyable cell renderer (plain text with hover copy button)
  const copyableTextCell = useCallback(
    ({ value }: TicketCellProps) => <CopyableCell value={value} />,
    []
  );

  // Format the DB timestamp exactly as the legacy inline Cell did, then wrap in CopyableCell
  const formatTicketTimestamp = useCallback((value: string | number | null | undefined) => {
    if (!value) return '';
    if (typeof value === 'string') {
      if (value.includes('T')) {
        return value.replace('T', ' ').substring(0, 19);
      }
      return value;
    }
    const date = new Date(value);
    return date.toISOString().replace('T', ' ').substring(0, 19);
  }, []);

  const ticketTimestampCell = useCallback(
    ({ value }: TicketCellProps<string | number | null | undefined>) => {
      const formatted = formatTicketTimestamp(value);
      if (!formatted) return '';
      return <CopyableCell value={formatted} />;
    },
    [formatTicketTimestamp]
  );

  // --- Column Definitions ---
  // Define ALL possible columns for Current Tickets
  const allCurrentColumns = useMemo<TicketColumn[]>(
    () => [
      { Header: 'Ticket No', accessor: 'Ticket No', Cell: copyableTextCell },
      { Header: 'Delivery No', accessor: 'Delivery No', Cell: copyableTextCell },
      { Header: 'Order No', accessor: 'Order No', Cell: copyableTextCell },
      { Header: 'Timestamp', accessor: 'Ticket Timestamp', Cell: ticketTimestampCell },
      { Header: 'Type', accessor: 'Ticket Type' },
      { Header: 'Dispatch Loc', accessor: 'Dispatch Location', Cell: copyableTextCell },
      { Header: 'Dispatch Org', accessor: 'Dispatch Organisation' },
      { Header: 'Receipt Loc', accessor: 'Receipt Location', Cell: copyableTextCell },
      { Header: 'Receipt Org', accessor: 'Receipt Organisation' },
      { Header: 'Reg No', accessor: 'Reg No', Cell: regNoCell },
      { Header: 'Ref No', accessor: 'Ref No' },
      { Header: 'Product', accessor: 'Product' },
      { Header: 'First Wgt', accessor: 'First Weight' },
      { Header: 'Second Wgt', accessor: 'Second Weight' },
      { Header: 'Nett Wgt', accessor: 'Nett Weight' },
      { Header: 'Version', accessor: 'Version' },
      { Header: 'TicketUID', accessor: 'TicketUID' },
    ],
    [regNoCell, copyableTextCell, ticketTimestampCell]
  );

  // Define ALL possible columns for History Tickets
  const allHistoryColumns = useMemo<TicketColumn[]>(
    () => [
      { Header: 'Version', accessor: 'Version' },
      { Header: 'Ticket No', accessor: 'Ticket No', Cell: copyableTextCell },
      { Header: 'Delivery No', accessor: 'Delivery No', Cell: copyableTextCell },
      { Header: 'Order No', accessor: 'Order No', Cell: copyableTextCell },
      {
        Header: 'Amended At',
        accessor: 'AmendmentTimestamp',
        Cell: ({ value }: TicketCellProps<string | null | undefined>) => formatAppDateTime(value),
      },
      { Header: 'Amended By', accessor: 'AmendmentUser' },
      { Header: 'Reason for Next Version', accessor: 'AmendmentNotes' },
      { Header: 'Timestamp', accessor: 'Ticket Timestamp', Cell: ticketTimestampCell },
      { Header: 'Type', accessor: 'Ticket Type' },
      { Header: 'Dispatch Loc', accessor: 'Dispatch Location', Cell: copyableTextCell },
      { Header: 'Dispatch Org', accessor: 'Dispatch Organisation' },
      { Header: 'Receipt Loc', accessor: 'Receipt Location', Cell: copyableTextCell },
      { Header: 'Receipt Org', accessor: 'Receipt Organisation' },
      { Header: 'Reg No', accessor: 'Reg No', Cell: regNoCell },
      { Header: 'Ref No', accessor: 'Ref No' },
      { Header: 'Product', accessor: 'Product' },
      { Header: 'First Wgt', accessor: 'First Weight' },
      { Header: 'Second Wgt', accessor: 'Second Weight' },
      { Header: 'Nett Wgt', accessor: 'Nett Weight' },
      { Header: 'TicketUID', accessor: 'TicketUID' },
    ],
    [regNoCell, copyableTextCell, ticketTimestampCell]
  );

  // Excluded Tickets tab: the ticket as it stands, plus who excluded it, when and why
  const excludedColumns = useMemo<TicketColumn[]>(
    () => [
      { Header: 'Ticket No', accessor: 'Ticket No', Cell: copyableTextCell },
      { Header: 'Timestamp', accessor: 'Ticket Timestamp', Cell: ticketTimestampCell },
      { Header: 'Type', accessor: 'Ticket Type' },
      { Header: 'Dispatch Loc', accessor: 'Dispatch Location', Cell: copyableTextCell },
      { Header: 'Receipt Loc', accessor: 'Receipt Location', Cell: copyableTextCell },
      { Header: 'Reg No', accessor: 'Reg No', Cell: regNoCell },
      { Header: 'Delivery No', accessor: 'Delivery No', Cell: copyableTextCell },
      { Header: 'Nett Wgt', accessor: 'Nett Weight' },
      {
        Header: 'Excluded At',
        accessor: 'AmendmentTimestamp',
        Cell: ({ value }: TicketCellProps<string | null | undefined>) => formatAppDateTime(value),
      },
      { Header: 'Excluded By', accessor: 'AmendmentUser' },
      { Header: 'Reason', accessor: 'AmendmentNotes' },
    ],
    [regNoCell, copyableTextCell, ticketTimestampCell]
  );

  // Define the DEFAULT visible columns for Current Tickets
  const defaultCurrentColumns = useMemo(
    () =>
      allCurrentColumns.filter((col) =>
        [
          'Ticket No',
          'Delivery No',
          'Order No',
          'Ticket Timestamp',
          'Ticket Type',
          'Dispatch Location',
          'Receipt Location',
          'Reg No',
          'Ref No',
          'Nett Weight',
          'Version',
        ].includes(col.accessor)
      ),
    [allCurrentColumns]
  );

  // Define the DEFAULT visible columns for History Tickets
  const defaultHistoryColumns = useMemo(
    () =>
      allHistoryColumns.filter((col) =>
        [
          'Version',
          'Ticket No',
          'Delivery No',
          'Order No',
          'AmendmentTimestamp',
          'AmendmentUser',
          'Ticket Timestamp',
          'Ticket Type',
          'Dispatch Location',
          'Receipt Location',
          'Reg No',
          'Ref No',
          'Nett Weight',
          'AmendmentNotes',
        ].includes(col.accessor)
      ),
    [allHistoryColumns]
  );

  // State for currently visible columns
  const [visibleCurrentColumns, setVisibleCurrentColumns] = useState(defaultCurrentColumns);
  const [visibleHistoryColumns, setVisibleHistoryColumns] = useState(defaultHistoryColumns);

  // Handlers for column visibility changes
  const handleVisibleCurrentColumnsChange = useCallback((newVisibleColumns: TicketColumn[]) => {
    setVisibleCurrentColumns(newVisibleColumns);
    // Optional: Persist this preference (e.g., in localStorage)
  }, []);

  const handleVisibleHistoryColumnsChange = useCallback((newVisibleColumns: TicketColumn[]) => {
    setVisibleHistoryColumns(newVisibleColumns);
    // Optional: Persist this preference
  }, []);

  // ST-00218 lm-status: the "Load status" column of the current tickets (after the ticket type,
  // visible by default, in the column picker) while the server runs load matching.
  const loadStatus = useLoadStatusColumn(
    currentTickets,
    allCurrentColumns,
    visibleCurrentColumns,
    handleVisibleCurrentColumnsChange
  );

  // --- Data Fetching ---
  const fetchData = useCallback(
    async (
      tab: Tab,
      page = 1,
      currentFilters: AmendmentFilters = filters,
      currentSortConfig: SortConfig = sortConfig
    ) => {
      // Use currentSortConfig
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); // Abort previous request
        console.log('DEBUG: Aborted previous fetch request.');
      }
      abortControllerRef.current = new AbortController(); // Create a new controller for the current request
      const signal = abortControllerRef.current.signal;

      setLoading(true);
      setError(null);
      const isCurrentTab = tab === 'current';
      const isExcludedTab = tab === 'excluded';

      // Build parameters for paginated fetch
      const paginationParams = {
        page,
        limit: resultsPerPage, // Use resultsPerPage state for limit
        ...currentFilters, // Spread the filter parameters
        ...(currentSortConfig.sortBy && {
          // Add sort parameters if sortBy is set
          sortBy: currentSortConfig.sortBy,
          sortDir: currentSortConfig.sortDir,
          ...(currentSortConfig.thenBy && {
            // Add secondary sort if thenBy is set
            thenBy: currentSortConfig.thenBy,
            thenDir: currentSortConfig.thenDir,
          }),
        }),
      };

      console.log(`DEBUG: Fetching paginated data for ${tab} with params:`, paginationParams);

      try {
        // Fetch paginated data
        const fetchPaginatedFunction = isCurrentTab
          ? api.amendmentsV3.getLatestTickets
          : isExcludedTab
            ? api.amendmentsV3.getExcludedTickets
            : api.amendmentsV3.getAmendedTickets;
        // Pass the signal to the API call
        const paginatedResponse: AxiosResponse<TicketPage> = await fetchPaginatedFunction(
          paginationParams,
          { signal }
        );
        console.log(`Fetched paginated data for ${tab}:`, paginatedResponse.data);

        if (isCurrentTab) {
          setCurrentTickets(paginatedResponse.data.data || []);
          // Update pagination state, ensuring limit reflects resultsPerPage
          setCurrentPagination((_prev) => ({
            ...(paginatedResponse.data.pagination || { page: 1, totalPages: 1, totalCount: 0 }),
            limit: resultsPerPage,
          }));
        } else if (isExcludedTab) {
          setExcludedTickets(paginatedResponse.data.data || []);
          setExcludedPagination({
            ...(paginatedResponse.data.pagination || { page: 1, totalPages: 1, totalCount: 0 }),
            limit: resultsPerPage,
          });
        } else {
          setHistoryTickets(paginatedResponse.data.data || []);
          // Update pagination state, ensuring limit reflects resultsPerPage
          setHistoryPagination((_prev) => ({
            ...(paginatedResponse.data.pagination || { page: 1, totalPages: 1, totalCount: 0 }),
            limit: resultsPerPage,
          }));
        }
      } catch (caught) {
        const err = caught as RequestError | null | undefined;
        // Axios 1.x throws CanceledError on abort; older code only looked for AbortError.
        // Also tolerate axios.isCancel semantics via the __CANCEL__ marker.
        const isCancel =
          err?.name === 'AbortError' ||
          err?.name === 'CanceledError' ||
          err?.code === 'ERR_CANCELED' ||
          err?.__CANCEL__ === true;
        if (isCancel) {
          console.log(`Fetch aborted for ${tab} tickets`);
          // Don't set error state for aborted requests
        } else {
          console.error(`Error fetching ${tab} tickets:`, err);
          // A filter the server refuses (e.g. a dynamic filter on a column the history table
          // does not have, AMD-24) answers 400 { message }: show that reason.
          const reason = err?.response?.status === 400 ? err.response.data?.message : null;
          setError(reason || `Failed to fetch ${tab} tickets. Please check connection or filters.`);
        }
      } finally {
        setLoading(false);
        if (signal === abortControllerRef.current?.signal) {
          // Clear controller only if it's the one that completed
          abortControllerRef.current = null;
        }
      }
    },
    [filters, resultsPerPage, sortConfig]
  ); // Update dependencies: use sortConfig

  // --- Inline Edit: save + find-replace (needs fetchData) ---
  const handleSaveEdits = useCallback(async () => {
    if (pendingEdits.size === 0 || !editAmendmentNotes.trim()) return;
    // A blank or non-numeric weight would be saved as NULL and show the source weight (AMD-21);
    // the cell and Find & Replace refuse them, and so does the save.
    const badWeights = [...pendingEdits].filter(([key, newValue]) => {
      const field = key.split('::')[1] || '';
      return isWeightField(field) && !isValidWeight(newValue);
    });
    if (badWeights.length > 0) {
      const ticketNo = (uid: string) =>
        currentTickets.find((t) => t.TicketUID === uid)?.['Ticket No'] || uid;
      setEditSaveError(
        `Weights must be numbers (use a point as decimal separator, e.g. 51.35). Fix or undo: ${badWeights
          .map(([key]) => {
            const [uid = '', field = ''] = key.split('::');
            return `${ticketNo(uid)} ${field}`;
          })
          .join(', ')}`
      );
      return;
    }
    setIsSavingEdits(true);
    setEditSaveError(null);

    const amendmentsByTicket = new Map<string, Record<string, string>>();
    for (const [key, newValue] of pendingEdits) {
      const [ticketUID, field] = key.split('::') as [string, string];
      if (!amendmentsByTicket.has(ticketUID)) {
        amendmentsByTicket.set(ticketUID, {});
      }
      amendmentsByTicket.get(ticketUID)![field] = newValue;
    }

    const amendments = Array.from(amendmentsByTicket.entries()).map(([ticketUID, updates]) => ({
      ticketUID,
      updates,
    }));

    try {
      // A typed variable: api.ts declares this body as { ticketUIDs?, amendments? } only.
      const payload: BulkAmendIndividualPayload = {
        amendments,
        amendmentNotes: editAmendmentNotes.trim(),
      };
      const response: AxiosResponse<BulkAmendIndividualResponse> =
        await api.amendmentsV3.bulkAmendIndividual(payload);

      if (response.data.requiresApproval) {
        alert(response.data.message || 'Changes submitted for approval');
      }

      // HTTP 207: some tickets were not saved. Keep exactly their edits and say
      // which ones, instead of closing as if everything saved (AMD-20).
      const failed = Array.isArray(response.data.failed) ? response.data.failed : [];
      if (failed.length > 0) {
        const failedUIDs = new Set<string | undefined>(failed.map((f) => f.ticketUID));
        setPendingEdits(
          (prev) => new Map([...prev].filter(([key]) => failedUIDs.has(key.split('::')[0])))
        );
        const ticketNo = (uid: string) =>
          currentTickets.find((t) => t.TicketUID === uid)?.['Ticket No'] || uid;
        setEditSaveError(
          `${response.data.message || 'Some changes were not saved'} Not saved: ${failed
            .map((f) => `${ticketNo(f.ticketUID)}${f.error ? ` (${f.error})` : ''}`)
            .join(', ')}`
        );
        fetchData('current', currentPagination.page, filters, sortConfig);
        return;
      }

      setPendingEdits(new Map());
      setEditAmendmentNotes('');
      setEditMode(false);
      setShowFindReplace(false);
      fetchData('current', currentPagination.page, filters, sortConfig);
    } catch (caught) {
      const err = caught as RequestError;
      console.error('Error saving inline edits:', err);
      if (err.response?.status === 409) {
        const conflicts = err.response.data.conflicts || [];
        const conflictMsg =
          conflicts.length > 0
            ? `\n\nExisting pending batches:\n${conflicts.map((c) => `  - Batch #${c.approvalId} by ${c.submittedBy} (${c.overlappingUIDs?.length || 1} overlapping ticket(s))`).join('\n')}`
            : '';
        setEditSaveError(
          `${err.response.data.message}${conflictMsg}\n\nGo to Amendment Approvals to withdraw or reject the existing batch first.`
        );
      } else {
        setEditSaveError(
          rejectionMessage(err.response?.data) || err.message || 'Failed to save changes'
        );
      }
    } finally {
      setIsSavingEdits(false);
    }
  }, [
    pendingEdits,
    editAmendmentNotes,
    fetchData,
    currentPagination.page,
    filters,
    sortConfig,
    currentTickets,
  ]);

  // Find & Replace hands over each matching row's new text: only the matched part is replaced
  // (AMD-45), and a weight cell is set to the (numeric) replace value as a whole.
  const handleFindReplaceApply = useCallback(
    (columnAccessor: string, replacements: FindReplacement[]) => {
      setPendingEdits((prev) => {
        const next = new Map(prev);
        for (const { ticketUID, newValue } of replacements) {
          if (!ticketUID) continue;
          const row = currentTickets.find((t) => t.TicketUID === ticketUID);
          const original = String(row?.[columnAccessor] ?? '');
          if (newValue !== original) {
            next.set(`${ticketUID}::${columnAccessor}`, newValue);
          }
        }
        return next;
      });
      if (!editAmendmentNotes.trim()) {
        setEditAmendmentNotes(`Find & Replace: Updated ${columnAccessor}`);
      }
    },
    [editAmendmentNotes, currentTickets]
  );

  // Fetch data when tab, filters, sortConfig, or resultsPerPage change, BUT ONLY IF filters have been applied
  useEffect(() => {
    if (hasAppliedFilters) {
      // Fetch data for the active tab, resetting to page 1 when filters, sortConfig, or resultsPerPage change
      console.log(
        'useEffect triggered by state change. Fetching data with sortConfig:',
        JSON.stringify(sortConfig)
      ); // Log the sortConfig used by useEffect
      fetchData(activeTab, 1, filters, sortConfig);
    } else {
      // Optionally, clear data if filters haven't been applied yet or reset to an empty state
      setCurrentTickets([]);
      setHistoryTickets([]);
      setExcludedTickets([]);
      setCurrentPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
      setHistoryPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
      setExcludedPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
      console.log('useEffect: Filters not applied yet. Skipping initial data fetch.');
    }
  }, [activeTab, filters, sortConfig, resultsPerPage, hasAppliedFilters, fetchData]); // Add hasAppliedFilters and fetchData

  // Fetch data when page changes (filters remain the same)
  const handlePageChange = (tab: Tab, newPage: number) => {
    if (editMode && pendingEdits.size > 0) {
      if (
        !window.confirm(
          `You have ${pendingEdits.size} unsaved edit(s). Navigating will lose them. Continue?`
        )
      )
        return;
      setPendingEdits(new Map());
      setEditAmendmentNotes('');
    }
    if (tab === 'current') {
      setCurrentPagination((prev) => ({ ...prev, page: newPage }));
      fetchData('current', newPage, filters, sortConfig);
    } else if (tab === 'excluded') {
      setExcludedPagination((prev) => ({ ...prev, page: newPage }));
      fetchData('excluded', newPage, filters, sortConfig);
    } else {
      setHistoryPagination((prev) => ({ ...prev, page: newPage }));
      fetchData('history', newPage, filters, sortConfig);
    }
  };
  // Keep the ref fresh so find-in-results can navigate without dep churn
  handlePageChangeRef.current = handlePageChange;

  // Invalidate find results when filters/sort/page-size change; they'd point at stale ordinals.
  // clearFind also resets findTerm, so the search box visibly empties when the user applies
  // new filter criteria instead of leaving a stale term in the input.
  useEffect(() => {
    clearFind();
  }, [filters, sortConfig, resultsPerPage, clearFind]);

  // Strip the ?ticketUIDs= from the URL after consuming it — avoids re-applying on navigation
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('ticketUIDs')) {
        params.delete('ticketUIDs');
        params.delete('field');
        params.delete('value');
        params.delete('why');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? '?' + qs : ''}`);
      }
    } catch (_) {}
  }, []);

  const clearHandoff = useCallback(() => {
    setHandoffTicketUIDs(null);
    setFilters((prev) => {
      const next = { ...prev };
      delete next.ticketUIDs;
      return next;
    });
  }, []);

  // Ctrl/Cmd+F override — only on the Current tab
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (activeTab !== 'current') return;
      const isFind =
        (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'f' || e.key === 'F');
      if (!isFind) return;
      e.preventDefault();
      const el = findInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeTab]);

  const handleFilterChange = (newFilters: AmendmentFilters) => {
    setFilters(newFilters);
    if (!hasAppliedFilters) {
      setHasAppliedFilters(true);
    }
  };

  const handleClearFilters = () => {
    setHasAppliedFilters(false);
    setFilters({});
    setCurrentTickets([]);
    setHistoryTickets([]);
    setExcludedTickets([]);
    setCurrentPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
    setHistoryPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
    setExcludedPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
    setSelectedRows([]);
    setError(null);
    setExportError(null);
  };

  // ThemedSelect hands back the option's own value (a number here); parseInt reads it as
  // String(value), so the explicit String() is the same conversion.
  const handleResultsPerPageChange = (event: { target: { value: string | number } }) => {
    const newLimit = parseInt(String(event.target.value), 10);
    console.log('DEBUG: Results per page changed to:', newLimit);
    setResultsPerPage(newLimit);
    // Reset pagination for both tabs to use the new limit
    setCurrentPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    setHistoryPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    setExcludedPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    // Fetching is handled by the useEffect watching 'resultsPerPage'
  };

  // --- Sorting ---
  const handleSortByRegThenTimestamp = () => {
    // Determine the next direction for Reg No, but keep secondary always ASC
    const isCurrentSort =
      sortConfig.sortBy === '[Reg No]' && sortConfig.thenBy === '[Ticket Timestamp]';
    // Always default to ASC the first time this sort is selected, then toggle primary
    const nextSortDir = isCurrentSort ? (sortConfig.sortDir === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
    const newSortConfig: SortConfig = {
      sortBy: '[Reg No]',
      sortDir: nextSortDir,
      thenBy: '[Ticket Timestamp]',
      thenDir: 'ASC', // Explicitly ensure secondary is ASC
    };
    console.log('Applying new sortConfig:', newSortConfig); // Log the config being set
    setSortConfig(newSortConfig);
    // Fetching is handled by the useEffect watching 'sortConfig'
  };

  const handleSortByTimestamp = () => {
    // Pure Ticket Timestamp sort
    const isCurrentSort = sortConfig.sortBy === '[Ticket Timestamp]' && !sortConfig.thenBy;
    // Toggle between ASC and DESC
    const nextSortDir = isCurrentSort ? (sortConfig.sortDir === 'ASC' ? 'DESC' : 'ASC') : 'DESC';
    const newSortConfig: SortConfig = {
      sortBy: '[Ticket Timestamp]',
      sortDir: nextSortDir,
      thenBy: null,
      thenDir: 'ASC',
    };
    console.log('Applying timestamp sortConfig:', newSortConfig);
    setSortConfig(newSortConfig);
    // Fetching is handled by the useEffect watching 'sortConfig'
  };

  const handleClearSort = () => {
    console.log('Clearing sort - resetting to backend default');
    // Set only sortBy and thenBy to null. Backend addSorting will apply its default.
    setSortConfig({ sortBy: null, sortDir: 'DESC', thenBy: null, thenDir: 'ASC' }); // Keep DESC/ASC for potential future use, but null sortBy/thenBy triggers default
    // Fetching is handled by the useEffect watching 'sortConfig'
  };

  // --- Modal Handling ---
  const handleOpenHistoryModal = (ticketUID: string) => {
    setSelectedTicketUID(ticketUID);
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedTicketUID(null);
  };

  const handleViewTransactionDetails = (transactionData: AmendmentTicketRow) => {
    setSelectedTransactionData(transactionData);
    setIsTransactionDetailsModalOpen(true);
  };

  const handleCloseTransactionDetailsModal = () => {
    setIsTransactionDetailsModalOpen(false);
    setSelectedTransactionData(null);
  };

  const handleShareTransaction = (transactionData: AmendmentTicketRow) => {
    setShareTransactionData(transactionData);
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setShareTransactionData(null);
  };

  const handleBulkSaveSuccess = () => {
    // Refresh the current tickets list and clear selection (respecting current sort/filter)
    fetchData('current', currentPagination.page, filters, sortConfig);
    setSelectedRows([]);
    // Optionally: Show a success notification
  };

  const handleOpenBulkModal = () => {
    if (selectedRows.length > 0) {
      console.log('DEBUG: Opening Bulk Amend Modal for rows:', selectedRows);
      setIsBulkModalOpen(true);
    }
  };

  const handleExcludeTicket = useCallback(
    async (ticketData: AmendmentTicketRow) => {
      const isCurrentlyExcluded = ticketData.IsExcluded === 1 || ticketData.IsExcluded === true;
      const action = isCurrentlyExcluded ? 'restore' : 'exclude';
      const ticketNo = ticketData['Ticket No'] || ticketData.TicketUID;

      const notes = window.prompt(
        isCurrentlyExcluded
          ? `Restore ticket ${ticketNo} from exclusion? Enter reason:`
          : `Mark ticket ${ticketNo} as duplicate and exclude from view? Enter reason:`,
        isCurrentlyExcluded ? 'Restoring excluded ticket' : 'Duplicate ticket - excluding from view'
      );
      if (!notes) return;

      try {
        const response: AxiosResponse<ExcludeTicketResponse> = await api.amendmentsV3.excludeTicket(
          ticketData.TicketUID,
          !isCurrentlyExcluded,
          notes
        );
        // Non-admins submit for approval: say so instead of silently leaving the row (AMD-44)
        if (response.data?.requiresApproval || response.data?.unchanged) {
          alert(response.data.message);
        }
        if (activeTab === 'excluded') {
          fetchData('excluded', excludedPagination.page, filters, sortConfig);
        } else {
          fetchData('current', currentPagination.page, filters, sortConfig);
        }
      } catch (caught) {
        const err = caught as RequestError;
        setError(rejectionMessage(err.response?.data) || `Failed to ${action} ticket`);
      }
    },
    [fetchData, activeTab, currentPagination.page, excludedPagination.page, filters, sortConfig]
  );

  const handleBulkExclude = useCallback(async () => {
    if (!selectedRows || selectedRows.length === 0) return;
    const notes = window.prompt(
      `Exclude ${selectedRows.length} ticket(s) as duplicates? Enter reason:`,
      'Duplicate tickets - excluding from view'
    );
    if (!notes) return;

    let succeeded = 0;
    let submitted = 0;
    let alreadyPending = 0;
    let failed = 0;
    for (const row of selectedRows) {
      try {
        const response: AxiosResponse<ExcludeTicketResponse> = await api.amendmentsV3.excludeTicket(
          row.TicketUID,
          true,
          notes
        );
        if (response.data?.requiresApproval) submitted++;
        else succeeded++;
      } catch (caught) {
        const err = caught as RequestError;
        // 409: an approval for this ticket is already waiting — not a failure (AMD-44)
        if (err.response?.status === 409) alreadyPending++;
        else failed++;
      }
    }
    fetchData('current', currentPagination.page, filters, sortConfig);
    setSelectedRows([]);
    if (submitted > 0 || alreadyPending > 0) {
      alert(
        [
          succeeded > 0 && `${succeeded} excluded`,
          submitted > 0 && `${submitted} submitted for approval`,
          alreadyPending > 0 && `${alreadyPending} already awaiting approval`,
          failed > 0 && `${failed} failed`,
        ]
          .filter(Boolean)
          .join(', ')
      );
    }
    if (failed > 0) {
      setError(`Bulk exclude: ${succeeded + submitted} succeeded, ${failed} failed`);
    }
  }, [selectedRows, fetchData, currentPagination.page, filters, sortConfig]);

  const handleCloseBulkModal = () => {
    setIsBulkModalOpen(false);
  };

  // Callback from DataTable - Memoized with useCallback
  const handleSelectedRowsChange = useCallback((selectedRowsData: AmendmentTicketRow[]) => {
    // console.log("DEBUG: Selected rows changed:", selectedRowsData); // Optional: for debugging selection
    setSelectedRows(selectedRowsData);
  }, []); // Empty dependency array: function reference will be stable

  // --- Export Logic ---
  const handleExport = async (format: string, tabType: Tab = 'history') => {
    setIsExporting(true);
    setExportError(null);
    console.log(`Attempting to export ${tabType} as ${format} with filters:`, filters);

    try {
      let response: AxiosResponse<TicketPage>;
      let sheetName: string;
      let filePrefix: string;

      if (tabType === 'current') {
        // Fetch ALL current tickets using the export endpoint
        response = await api.amendmentsV3.getLatestTicketsForExport(filters);
        sheetName = 'Current Tickets';
        filePrefix = 'current_tickets';
      } else if (tabType === 'excluded') {
        response = await api.amendmentsV3.getExcludedTickets({ ...filters, page: 1, limit: 10000 });
        sheetName = 'Excluded Tickets';
        filePrefix = 'excluded_tickets';
      } else {
        // Fetch ALL amended tickets using the export endpoint
        response = await api.amendmentsV3.getAmendedTicketsForExport(filters);
        sheetName = 'Amendment History';
        filePrefix = 'amendment_history';
      }

      const dataToExport = response.data.data || [];
      console.log(`Fetched ${dataToExport.length} records for ${tabType} export.`);
      // The excluded list is paginated (server cap 10,000): say when it was cut (review N23)
      const exportTotal = response.data.pagination?.totalCount;
      // (`undefined > n` was false: an absent total never reports a cut)
      if (exportTotal !== undefined && exportTotal > dataToExport.length) {
        setExportError(
          `Exported the first ${dataToExport.length.toLocaleString()} of ${exportTotal.toLocaleString()} tickets. Narrow the filters to export the rest.`
        );
      }

      if (dataToExport.length === 0) {
        setExportError(`No ${tabType} data found matching the current filters to export.`);
        setIsExporting(false);
        return;
      }

      // Prepare data for worksheet (e.g., format dates, select columns)
      // For simplicity, export all columns as is for now. Can refine later.
      const worksheetData = dataToExport.map((row) => {
        // Optionally format dates here if needed, e.g., using new Date().toLocaleString()
        // Be mindful of timezone differences if formatting client-side
        return row;
      });

      // ST-00218 lm-status: current tickets get a "Load status" text column when load matching
      // is on (labels looked up for every exported row, not only the ones on screen).
      const sheetRows =
        tabType === 'current' ? (await addLoadStatusToExport(worksheetData)).rows : worksheetData;
      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate file name with timestamp
      const dateStr = toApiDateString(new Date()); // YYYY-MM-DD
      const fileName = `${filePrefix}_${dateStr}.${format}`;

      // Trigger download
      XLSX.writeFile(workbook, fileName);
    } catch (caught) {
      const err = caught as RequestError;
      console.error(`Error during ${tabType} export:`, err);
      setExportError(err.response?.data?.message || err.message || 'Failed to export data.');
    } finally {
      setIsExporting(false);
    }
  };

  // --- Column Definitions are now above Data Fetching ---

  const [isFilterOpen, setIsFilterOpen] = useState(true);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaEdit}
        title="Amendments"
        subtitle="Manage ticket amendments and view history"
        className="mb-3"
        toolbarAlign="start"
        toolbar={
          <SegmentedControl
            ariaLabel="Amendments view"
            value={activeTab}
            onChange={setActiveTab}
            options={[
              {
                value: 'current',
                label: 'Current Tickets',
                shortLabel: 'Current',
                badge:
                  hasAppliedFilters && currentPagination.totalCount > 0
                    ? currentPagination.totalCount.toLocaleString()
                    : null,
              },
              {
                value: 'history',
                label: 'Amendment History',
                shortLabel: 'History',
                badge:
                  hasAppliedFilters && historyPagination.totalCount > 0
                    ? historyPagination.totalCount.toLocaleString()
                    : null,
              },
              {
                value: 'excluded',
                label: 'Excluded Tickets',
                shortLabel: 'Excluded',
                badge:
                  hasAppliedFilters && excludedPagination.totalCount > 0
                    ? excludedPagination.totalCount.toLocaleString()
                    : null,
              },
            ]}
          />
        }
        actions={
          <button
            type="button"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            aria-pressed={isFilterOpen}
            aria-label={isFilterOpen ? 'Hide filters' : 'Show filters'}
            title={isFilterOpen ? 'Hide filters' : 'Show filters'}
            className={`inline-flex items-center justify-center gap-1.5 h-10 min-w-10 px-2.5 text-sm font-medium rounded-lg border transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              isFilterOpen
                ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                : 'bg-surface text-slate-600 border-slate-300 hover:border-slate-400'
            }`}
          >
            <FaFilter className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="page-header__label">
              {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
            </span>
          </button>
        }
      />

      {/* Collapsible Filter Controls */}
      {isFilterOpen && (
        <div className="mb-3">
          <FilterControls
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
            isLoading={loading}
            onCancelRequest={() => {
              if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                console.log('User cancelled the request');
              }
            }}
          />
        </div>
      )}

      {/* Data Area - Table controls integrated as toolbar */}
      <div className="bg-surface border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        {/* Integrated Toolbar: Sort + Results per page + Export + Columns */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50/50">
          {/* Left: Sort controls */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Sort
            </span>
            <button
              onClick={handleSortByTimestamp}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                sortConfig.sortBy === '[Ticket Timestamp]' && !sortConfig.thenBy
                  ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                  : 'bg-surface text-slate-600 border-slate-300 hover:border-slate-400'
              }`}
            >
              Timestamp
              {sortConfig.sortBy === '[Ticket Timestamp]' && !sortConfig.thenBy && (
                <span className="text-[10px]">{sortConfig.sortDir === 'ASC' ? '↑' : '↓'}</span>
              )}
            </button>
            <button
              onClick={handleSortByRegThenTimestamp}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                sortConfig.sortBy === '[Reg No]' && sortConfig.thenBy === '[Ticket Timestamp]'
                  ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                  : 'bg-surface text-slate-600 border-slate-300 hover:border-slate-400'
              }`}
            >
              Reg + Time
              {sortConfig.sortBy === '[Reg No]' && sortConfig.thenBy === '[Ticket Timestamp]' && (
                <span className="text-[10px]">{sortConfig.sortDir === 'ASC' ? '↑' : '↓'}</span>
              )}
            </button>
            {sortConfig.sortBy && (
              <button
                onClick={handleClearSort}
                className="inline-flex items-center px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                title="Clear Sorting"
              >
                <svg
                  className="w-3 h-3 mr-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </button>
            )}

            <span className="text-slate-300 mx-1">|</span>

            {/* Results Per Page */}
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Show
            </span>
            <ThemedSelect
              id="resultsPerPage"
              value={resultsPerPage}
              onChange={handleResultsPerPageChange}
              className="block w-auto pl-2 pr-7 py-1 text-xs border border-slate-300 bg-surface rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={150}>150</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={10000}>10,000</option>
            </ThemedSelect>
          </div>

          {/* Right: Edit Mode + Export + Column selector */}
          <div className="flex items-center gap-2">
            {canEdit &&
              activeTab === 'current' &&
              hasAppliedFilters &&
              currentTickets.length > 0 && (
                <>
                  <button
                    onClick={handleToggleEditMode}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                      editMode
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : 'bg-surface text-slate-600 border-slate-300 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    {editMode ? 'Exit Edit Mode' : 'Table Edit Mode'}
                  </button>
                  {editMode && (
                    <button
                      onClick={() => setShowFindReplace((prev) => !prev)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                        showFindReplace
                          ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                          : 'bg-surface text-slate-600 border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                        />
                      </svg>
                      Find & Replace
                    </button>
                  )}
                  {editMode && pendingEdits.size > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-700 tabular-nums">
                      {pendingEdits.size} pending
                    </span>
                  )}
                </>
              )}

            <span className="text-slate-200">|</span>

            <button
              onClick={() => handleExport('xlsx', activeTab)}
              disabled={isExporting || loading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 disabled:opacity-50 transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {isExporting ? 'Exporting...' : 'Export XLSX'}
            </button>
            {activeTab === 'current' && (
              <ColumnSelector
                allColumns={loadStatus.allColumns}
                visibleColumns={loadStatus.visibleColumns}
                onVisibleColumnsChange={loadStatus.onVisibleColumnsChange}
              />
            )}
            {activeTab === 'history' && (
              <ColumnSelector
                allColumns={allHistoryColumns}
                visibleColumns={visibleHistoryColumns}
                onVisibleColumnsChange={handleVisibleHistoryColumnsChange}
              />
            )}
          </div>
        </div>

        {/* Bulk-amend handoff banner (from Recon View) */}
        {handoffTicketUIDs && handoffTicketUIDs.length > 0 && filters.ticketUIDs && (
          <div className="mx-4 mt-3 p-2.5 bg-primary-50 border border-primary-200 text-primary-800 text-xs rounded-md flex items-center gap-3">
            <svg
              className="w-4 h-4 flex-shrink-0 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
            <span>
              Showing <span className="font-semibold tabular-nums">{handoffTicketUIDs.length}</span>{' '}
              ticket{handoffTicketUIDs.length === 1 ? '' : 's'}{' '}
              {handoffSuggestion
                ? 'opened from Load matching'
                : 'opened from Route Summary — Recon'}
              .
              {handoffSuggestion && (
                <span data-testid="handoff-suggestion">
                  {' '}
                  {handoffSuggestion.field === 'exclude' ? (
                    <>
                      Suggested action: exclude this ticket — it looks like a duplicate weighing
                      {handoffSuggestion.value && (
                        <>
                          {' '}
                          of dispatch{' '}
                          <span className="font-mono font-semibold">{handoffSuggestion.value}</span>
                        </>
                      )}
                      . Check it before you exclude.
                    </>
                  ) : handoffSuggestion.field === 'review' ? (
                    <>
                      The truck was dispatched again before a receipt: check which dispatch is real.
                    </>
                  ) : handoffSuggestion.field === 'ticket_timestamp' ? (
                    <>
                      Check the ticket date
                      {handoffSuggestion.value && (
                        <>
                          {' '}
                          <span className="font-mono font-semibold">{handoffSuggestion.value}</span>
                        </>
                      )}
                      : it is before 2000 or in the future, so load matching cannot use it.
                    </>
                  ) : handoffSuggestion.field === 'dispatch_location' ||
                    handoffSuggestion.field === 'receipt_location' ? (
                    handoffSuggestion.value ? (
                      <>
                        Suggested correction
                        {handoffTicketUIDs.length > 1 &&
                          ` for these ${handoffTicketUIDs.length} tickets`}
                        : set the{' '}
                        {handoffSuggestion.field === 'dispatch_location'
                          ? 'dispatch location'
                          : 'receipt location'}{' '}
                        to{' '}
                        <span className="font-mono font-semibold">{handoffSuggestion.value}</span>
                        {handoffSuggestion.why && <> — {handoffSuggestion.why}</>}. Check it before
                        you amend.
                      </>
                    ) : (
                      <>
                        Check the{' '}
                        {handoffSuggestion.field === 'dispatch_location'
                          ? 'dispatch location'
                          : 'receipt location'}
                        .
                      </>
                    )
                  ) : handoffSuggestion.value ? (
                    <>
                      Suggested correction: set the{' '}
                      {handoffSuggestion.field === 'reg_no' ? 'registration' : 'delivery no'} to{' '}
                      <span className="font-mono font-semibold">{handoffSuggestion.value}</span>.
                      Check it before you amend.
                    </>
                  ) : (
                    <>
                      Check the{' '}
                      {handoffSuggestion.field === 'reg_no' ? 'registration' : 'delivery no'}.
                    </>
                  )}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={clearHandoff}
              className="ml-auto inline-flex items-center rounded-md bg-surface px-2 py-1 text-[11px] font-medium text-primary-700 border border-primary-300 hover:bg-primary-50 transition-colors"
            >
              Clear filter
            </button>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="mx-4 mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </div>
        )}
        {exportError && (
          <div className="mx-4 mt-3 p-2.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-md flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Export: {exportError}
          </div>
        )}

        {/* Table Content */}
        <div className="p-0">
          {activeTab === 'current' && (
            <>
              {!editMode && (
                <FindBar
                  ref={findInputRef}
                  value={findTerm}
                  onChange={setFindTerm}
                  onSubmit={runFind}
                  onNext={findNext}
                  onPrev={findPrev}
                  onClear={clearFind}
                  totalMatches={findMatches ? findMatches.length : null}
                  currentIndex={findIndex}
                  loading={findLoading}
                  capped={findCapped}
                  lastSearchedTerm={lastSearchedTerm}
                  errorMessage={findError}
                  placeholder="Find in results  —  Ticket No, Delivery No, Order No, Ref No, Reg No"
                />
              )}
              {canEdit && editMode && showFindReplace && (
                <FindReplaceBar
                  data={currentTickets}
                  editableColumns={visibleCurrentColumns}
                  onApplyReplace={handleFindReplaceApply}
                  onClose={() => setShowFindReplace(false)}
                />
              )}
              {editSaveError && (
                <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md flex items-center gap-2">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Save failed: {editSaveError}
                </div>
              )}
              <DataTable
                columns={loadStatus.visibleColumns}
                data={currentTickets}
                pagination={currentPagination}
                onPageChange={(newPage) => handlePageChange('current', newPage)}
                onViewHistory={handleOpenHistoryModal}
                onViewTransactionDetails={handleViewTransactionDetails}
                onShareTransaction={handleShareTransaction}
                onExcludeTicket={canEdit && !editMode ? handleExcludeTicket : undefined}
                loading={loading}
                onSelectedRowsChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                onBulkAmend={canEdit && !editMode ? handleOpenBulkModal : undefined}
                onBulkExclude={canEdit && !editMode ? handleBulkExclude : undefined}
                highlightTicketUID={highlightTicketUID}
                editMode={canEdit && editMode}
                pendingEdits={pendingEdits}
                onCellEdit={handleCellEdit}
                readOnly={!canEdit}
              />
              {canEdit && editMode && pendingEdits.size > 0 && (
                <EditModeDiffPanel
                  pendingEdits={pendingEdits}
                  originalData={currentTickets}
                  amendmentNotes={editAmendmentNotes}
                  onNotesChange={setEditAmendmentNotes}
                  onSave={handleSaveEdits}
                  onDiscard={handleDiscardEdits}
                  onUndoEdit={handleUndoEdit}
                  isSaving={isSavingEdits}
                />
              )}
            </>
          )}

          {activeTab === 'history' && (
            <DataTable
              columns={visibleHistoryColumns}
              data={historyTickets}
              pagination={historyPagination}
              onPageChange={(newPage) => handlePageChange('history', newPage)}
              loading={loading}
            />
          )}

          {activeTab === 'excluded' && (
            <>
              <p className="px-4 py-2 text-xs text-slate-500 border-b border-slate-200">
                Tickets marked as duplicates are left out of every report.
                {canEdit && ' Use the restore button on a row to bring a ticket back.'}
              </p>
              <DataTable
                columns={excludedColumns}
                data={excludedTickets}
                pagination={excludedPagination}
                onPageChange={(newPage) => handlePageChange('excluded', newPage)}
                onViewHistory={handleOpenHistoryModal}
                onExcludeTicket={canEdit ? handleExcludeTicket : undefined}
                loading={loading}
                readOnly
              />
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <VersionHistoryModalAllocateTransactions
        isOpen={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        ticketUID={selectedTicketUID}
      />
      <BulkAmendmentModalAllocateTransactions
        isOpen={canEdit && isBulkModalOpen}
        onClose={handleCloseBulkModal}
        selectedTickets={selectedRows}
        onSaveSuccess={handleBulkSaveSuccess}
      />
      <TransactionDetailsModal
        isOpen={isTransactionDetailsModalOpen}
        onClose={handleCloseTransactionDetailsModal}
        transactionData={selectedTransactionData}
      />
      <ShareTransactionModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        transactionData={shareTransactionData}
      />
      <RegNoTransactionsModalAmendments
        isOpen={isRegNoModalOpen}
        onClose={handleCloseRegNoModal}
        regNo={selectedRegNo}
        clickedTicketNo={clickedTicketNoForRegNo}
        inheritedFilters={filters}
      />
    </div>
  );
};

export default AmendmentsAllocateTransactions; // Renamed export
